import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers.ts';
import IResponse, { IPagination } from '../../interfaces/response.ts';
import { useDebounce } from 'use-debounce';
import { filterList } from '../../utils/array.ts';
import { isNull } from 'lodash';
import axios from 'axios';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BaranDataGrid from '../common/BaranDataGrid.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { exportToExcel } from '../../utils/file.ts';
import { IPackage } from '../../interfaces/studio.ts';
import { useCampaignStatusColors } from '../../hooks/useCampaignStatusColors.tsx';

export default function PaidRequests() {
	const { t } = useTranslation();
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<IPackage[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const { type } = useParams();

	const headerClassNames = 'text-xs bg-background-paper';
	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};
	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'companies_list', t });
	};
	const handleClickOpen = (id: number) => () => {
		navigate(`/studio/view/${id}`);
	};

	const navigate = useNavigate();

	const properties: (keyof IPackage)[] = ['title', 'media', 'price'];
	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const handleClickDelete = (id: number) => {
		if (!isNull(id)) {
			axios({
				url: `/admin/studio-requests/${id}`,
				method: 'DELETE',
				loading: true,
			}).then(() => {
				getServer();
			});
		}
	};

	const handleNew = () => {
		navigate('/admin/studio/package/new');
	};

	const columns: GridColDef<IPackage>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'company',
			headerName: t('company'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'package',
			headerName: t('package'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 160,
			display: 'flex',
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			align: 'center',
			renderCell: params => {
				const bgColorClass = useCampaignStatusColors(params.value);
				return <div className={`flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{t(params.value)}</div>;
			},
		},
		{
			field: 'created_at',
			headerName: t('date'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				return (
					<>
						<IconButton onClick={handleClickOpen(params?.row?.id)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={() => handleClickDelete(params?.row?.id)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		const searchJson = JSON.stringify([{ field: 'status', operator: '=', value: 'confirmation_pend' }]);
		setLoading(true);

		axios<IResponse<IPackage[]>>({
			url: '/admin/studio-requests',
			method: 'GET',
			params: {
				page: page,
				limit: pageSize,
				search: searchJson,
				type: type,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize, type]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<Box className="w-full">
			<BaranDataGrid<IPackage> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} onClick2={handleNew} actionButtonLable2={t('create_request_package')} searchableFields={properties} isCreateBtn2={true} />
		</Box>
	);
}
